import type { Team } from '@compass/client/types/team';
import type { UserSummary } from '@compass/client/types/user';
import { useMemo } from 'react';

import { useProgramTeams, useTeamMembersMap } from '@/hooks/useTeams';

interface AssigneeMember {
  userId: string;
  name: string;
  email: string;
}

export interface AssigneeGroup {
  teamId: string;
  teamName: string;
  members: AssigneeMember[];
}

export interface UseAssigneeOptionsReturn {
  groups: AssigneeGroup[];
  allUserIds: string[];
  usersMap: Map<string, UserSummary>;
  isLoading: boolean;
}

/**
 * Filters teams by workflow type using the same logic as auto-assignment:
 * 1. Teams whose workflow_types includes the given type
 * 2. Fallback to catch-all teams (empty workflow_types)
 * If no workflowType is provided, returns all teams.
 */
function filterTeamsByWorkflowType(teams: Team[], workflowType?: string): Team[] {
  if (!workflowType) return teams;

  const matched = teams.filter(
    t => t.workflow_types && t.workflow_types.length > 0 && t.workflow_types.includes(workflowType),
  );
  if (matched.length > 0) return matched;

  // Fallback: catch-all teams (no workflow_types set)
  const catchAll = teams.filter(t => !t.workflow_types || t.workflow_types.length === 0);
  if (catchAll.length > 0) return catchAll;

  // Final fallback: no matched teams and no catch-all teams, return all teams
  return teams;
}

/**
 * Pure: aggregate teams into grouped assignee options (members + manager per team,
 * sorted by name/email; empty teams dropped). Shared by the program-scoped
 * `useAssigneeOptions` and the org-wide `useOrgAssigneeOptions`.
 */
export function buildAssigneeGroups(
  teams: Team[],
  usersMap: Map<string, UserSummary>,
): { groups: AssigneeGroup[]; allUserIds: string[] } {
  const teamMemberSet = new Set<string>();

  const grps: AssigneeGroup[] = teams
    .map(team => {
      // Collect member IDs including manager
      const memberIds = new Set(team.member_ids || []);
      if (team.manager_id) memberIds.add(team.manager_id);

      const members: AssigneeMember[] = [...memberIds]
        .map(userId => {
          teamMemberSet.add(userId);
          const user = usersMap.get(userId);
          return {
            userId,
            name: user?.name || '',
            email: user?.email || userId,
          };
        })
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));

      return {
        teamId: team.id || '',
        teamName: team.name || 'Unnamed Team',
        members,
      };
    })
    .filter(g => g.members.length > 0);

  return { groups: grps, allUserIds: [...teamMemberSet] };
}

/**
 * Pure: keep only the members `keep` accepts, dropping teams left empty. Used to hide
 * users below Program Admin from a case whose human audit is reserved for Program Admins
 * (BV-2657).
 */
export function filterAssigneeGroups(
  groups: AssigneeGroup[],
  keep: (userId: string) => boolean,
): { groups: AssigneeGroup[]; allUserIds: string[] } {
  const kept = groups
    .map(group => ({ ...group, members: group.members.filter(member => keep(member.userId)) }))
    .filter(group => group.members.length > 0);
  const allUserIds = [...new Set(kept.flatMap(group => group.members.map(m => m.userId)))];
  return { groups: kept, allUserIds };
}

/**
 * Aggregates team data into a grouped structure for assignee dropdowns.
 * Groups users by their program teams.
 * When workflowType is provided, only includes members from teams that
 * match the workflow type (or catch-all teams as fallback).
 */
export const useAssigneeOptions = (workflowType?: string): UseAssigneeOptionsReturn => {
  const { teams, isLoading: teamsLoading } = useProgramTeams();
  const { usersMap, isLoading: teamMembersLoading } = useTeamMembersMap(teams);

  const filteredTeams = useMemo(
    () => filterTeamsByWorkflowType(teams, workflowType),
    [teams, workflowType],
  );

  const { groups, allUserIds } = useMemo(
    () => buildAssigneeGroups(filteredTeams, usersMap),
    [filteredTeams, usersMap],
  );

  return {
    groups,
    allUserIds,
    usersMap,
    isLoading: teamsLoading || teamMembersLoading,
  };
};
